﻿import React from 'react';
import "./Table.scss";
import GridFooter from "./GridFooter";
import GridCheckBox from "./GridCheckBox";
import { useSelector } from "react-redux";
import { RootState, useThunkDispatch } from "../../../store";
import { updateParameterValue } from "../../../store/storyline/actions";
import { GridApiPro, GridCellParams, GridColDef, GridRenderCellParams, GridRowModel, GridRowParams, GridRowSelectionModel, GridFilterInitialState, GridFilterItem, GridSlotProps, GridRowId, useGridApiRef, GridRenderEditCellParams, MuiEvent, GridCallbackDetails, renderEditInputCell, GridDensity, GridPaginationModel, GridSortModel } from '@mui/x-data-grid-pro';
import { RefObject } from 'react';
import { DataGrid, DataGridProps } from "../../../shared/components";
import * as _ from "lodash";
import clsx from "clsx";
import { CellWithTooltipRenderer } from "./CellWithTooltip";
import { DocumentedComponent } from '../../../shared/components/DocumentedComponent';
import { JsxRenderer } from '../ContentRenderer/JsxRenderer';
import { useCanvasBindings } from '../../../shared/providers/CanvasBindingsProvider';
import { useLazyData } from '../../../shared/hooks';
import { useReportValidationError } from "../../../shared/hooks/useReportValidationError";
import type { SourceInfo } from "@recursive-robot/react-jsx-parser";

interface ParameterMap {
    fieldName: string;
    parameterName: string;
}

type FormatterParams = {
    value: any;
    row: GridRowModel;
    column: GridColDef;
    apiRef: RefObject<GridApiPro>;
};

interface Column extends Omit<GridColDef, 'valueFormatter'> {
    index?: number;
    renderTooltip?: (params: GridCellParams) => React.ReactNode;
    valueFormatter?: (params: FormatterParams) => string;

    /**
     * @deprecated use `renderCell` instead.
    */
    cellTemplate?: string;
    /**
     * @deprecated use `renderEditCell` instead.
    */
    cellEditTemplate?: string;
    /**
     * @deprecated use `renderEditCell` instead.
    */
    cellEditTemplateFieldName?: string;
    /**
     * @deprecated use `renderTooltip` instead.
    */
    tooltipTemplate?: string;
}

type GetRowsArgs = {
    page: number;
    pageSize: number;
    orderBy: { field: string; sort: "asc" | "desc" }[];
};

type GetRowsResult = GridRowModel[] | { rows: GridRowModel[]; rowCount: number };

interface TableProps extends DataGridProps {
    name?: string;

    columns: Column[];
    additionalColumns?: Column[];

    getRows?: (args?: GetRowsArgs) => GetRowsResult | Promise<GetRowsResult>;
    serverSidePagination?: boolean;

    /**
     * @deprecated use `onRowClick` with `navigateToPage` instead.
    */
    navigateTo?: string;
    /**
     * @deprecated use `onRowClick` with `navigateToPage` instead.
    */
    parameterMap?: ParameterMap[];

    /**
     * @deprecated use `selectedRowsParameter` instead.
    */
    selectedRowParameter?: string;
    selectedRowsParameter?: string;
    editedRowsParameter?: string;
    allRowsParameter?: string;

    onRowClick?: (param: GridRowParams, event?: React.MouseEvent, details?: any) => void | boolean;
    onRowEdited?: (row: GridRowModel, originalRow: GridRowModel) => void | boolean;

    hideExportToExcel?: boolean;
    additionalFooterItems?: React.ReactNode;

    className?: string;

    // backwards compatibility
    headerHeight?: number;

    sourceInfo?: SourceInfo;
}

function mapValueFormatter(valueFormatter: any): (value: any, row: GridRowModel, column: GridColDef, apiRef: RefObject<GridApiPro>) => any {
    if (_.isFunction(valueFormatter)) {
        // add backward compatibility for old signature
        return (value, row, column, apiRef) => valueFormatter({ value, row, column, apiRef });
    }
}

function mapCellClassName(cellClassName?: string | Function | Array<string>): (GridCellParams) => string {
    if (_.isFunction(cellClassName)) {
        return cellClassName;
    }
    if (_.isArray(cellClassName)) {
        return new Function(...cellClassName) as (GridCellParams) => string; // eslint-disable-line no-new-func
    }
    if (_.isString(cellClassName)) {
        return (_) => cellClassName;
    }

    return (_) => "";
}

function cellEditHighlighter(params: GridCellParams) {
    return clsx({ "is-dirty": _.find(params.row?.editedFields, f => f === params.field) });
}

function getFieldValueSetterForRow(params: GridRenderCellParams) {
    return (field: string, value: any) => {
        params.api.startCellEditMode({ id: params.row.id, field: field });
        params.api.setEditCellValue({ id: params.row.id, field, value }, new Event("rowSelectionChange"));
        params.api.stopCellEditMode({ id: params.row.id, field: field });
    }
}

function mapColumn(column: Column): GridColDef {
    const {
        valueFormatter,
        cellTemplate,
        renderCell,
        tooltipTemplate,
        renderTooltip,
        cellClassName,
        cellEditTemplate,
        cellEditTemplateFieldName,
        renderEditCell
    } = column;

    const RenderCell =
        renderCell ? renderCell :
            cellTemplate ? ((params: GridRenderCellParams) =>  params.cellMode === "view" ? <JsxRenderer data={{ ...params.row, row: params.row, gridApi: params.api, setFieldValue: getFieldValueSetterForRow(params), params }} template={cellTemplate} /> : null) :
                null;

    const RenderEditCell =
        renderEditCell ? renderEditCell :
            cellEditTemplate ? ((params: GridRenderEditCellParams) => <JsxRenderer data={{ ...params.row, row: params.row, gridApi: params.api, params }} template={cellEditTemplate ?? ""} />) :
                cellEditTemplateFieldName ? ((params: GridRenderEditCellParams) => params?.row?.[cellEditTemplateFieldName] ? <JsxRenderer data={{ ...params.row, row: params.row, gridApi: params.api, params }} template={params?.row?.[cellEditTemplateFieldName] ?? ""} /> : renderEditInputCell(params)) :
                    undefined;

    const RenderTooltip =
        renderTooltip ? ((params: GridRenderCellParams) => params.cellMode === "view" && renderTooltip(params)) :
            tooltipTemplate ? ((params: GridRenderCellParams) => params.cellMode === "view" && <JsxRenderer data={{ ...params.row, row: params.row, params }} template={tooltipTemplate} />) :
                null;

    const RenderCellWithTooltip =
        RenderTooltip ? (params: GridRenderCellParams) => <CellWithTooltipRenderer tooltipRenderer={RenderTooltip} cellRenderer={RenderCell} {...params} /> :
            null;

    const classNameHandler = mapCellClassName(cellClassName);
    const combinedCellClassName = (params: GridCellParams) => clsx(cellEditHighlighter(params), classNameHandler(params));

    let result: GridColDef = {
        ...column,
        valueFormatter: mapValueFormatter(valueFormatter),
        renderCell: RenderCellWithTooltip ?? RenderCell,
        cellClassName: combinedCellClassName,
        renderEditCell: RenderEditCell,
    };

    return result;
}

// need backward compatibility for filter model due to following name changes: https://mui.com/x/migration/migration-data-grid-v5/#filtering
function addBackwardsCompatibilityToFilterState(initialFilter?: GridFilterInitialState): GridFilterInitialState {
    if (!initialFilter || !initialFilter.filterModel) {
        return {};
    }   

    // check if any deprecated fields are used...
    if (!(
        initialFilter.filterModel.items.some(
            (item: any) => item.columnField !== undefined
            || item.operatorValue !== undefined
        ) 
        || (initialFilter.filterModel as any).linkOperator !== undefined
    )) {
        return initialFilter;
    }

    const newFilter: GridFilterInitialState = {
        ...initialFilter,
        filterModel: {
            ...initialFilter.filterModel,
            items: initialFilter.filterModel.items.map((item: any, index) => {
                const newItem: GridFilterItem = {
                    ...item,
                    field: item.columnField ?? item.field,
                    operator: item.operatorValue ?? item.operator ?? "equals",  // is now mandatory
                    id: item.id ?? index  // required for multiple filters
                };

                // delete deprecated fields
                delete (newItem as any).columnField;
                delete (newItem as any).operatorValue;
                return newItem;
            }),
            logicOperator: (initialFilter.filterModel as any).linkOperator ?? initialFilter.filterModel.logicOperator,
        }
    };

    // delete deprecated fields
    delete (newFilter.filterModel as any).linkOperator;

    return newFilter;
}

// Density values outside of the expected range raise a runtime exception (recursive update depth exceeded),
// so we need to explicitly sanitize/default to an allowed value...
const ALLOWED_DENSITIES: GridDensity[] = ["compact", "standard", "comfortable"];
function sanitizeDensityValue(density?: GridDensity): GridDensity
{
    if (!density || !ALLOWED_DENSITIES.includes(density))
        return "standard";

    return density;
}

function normalizeGetRowsResult(result: GetRowsResult | undefined): { rows: GridRowModel[]; rowCount: number } {
    if (!result) return { rows: [], rowCount: 0 };
    if (Array.isArray(result)) return { rows: result, rowCount: result.length };

    return { rows: result.rows ?? [], rowCount: result.rowCount ?? result.rows?.length ?? 0 };
}

function Table(props: TableProps) {
    const { selectedRowParameter, selectedRowsParameter, parameterMap, navigateTo, rows, columns, additionalColumns, className, density, initialState, localeText: _localeText, allRowsParameter, editedRowsParameter, onRowEdited, onRowClick, onRowSelectionModelChange, additionalFooterItems, slots, slotProps, headerHeight, columnHeaderHeight, disableMultipleRowSelection = true, getRows, serverSidePagination, onPaginationModelChange, onSortModelChange, sourceInfo, ...other } = props;
    const [allColumns, setAllColumns] = React.useState<GridColDef[]>(columns);
    const [currentRows, setCurrentRows] = React.useState(rows);
    // default model
    const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>({ type: 'include', ids: new Set() });
    const apiRef = useGridApiRef();
    const parameterValues = useSelector((store: RootState) => store.storyline.parameterValues);
    const dispatch = useThunkDispatch();
    const { navigateToPage } = useCanvasBindings();

    // Input validation and error reporting...
    const reportValidationError = useReportValidationError(sourceInfo, "Autocomplete");

    React.useEffect(() => {
        const result = columns?.map(c => mapColumn(c)) ?? [];
        _.forEach(additionalColumns, additionalColumn => {
            result.splice(additionalColumn.index ?? result.length, 0, mapColumn(additionalColumn));
        });
        setAllColumns(result);

    }, [columns, additionalColumns]);

    React.useEffect(() => {
        // The `rows` prop is ignored (and typically absent) when data is fetched via `getRows`...
        if (getRows) return;

        if (currentRows?.length === rows?.length && _.isEqual(currentRows, rows)) return;
        setCurrentRows(rows);

        // Clear out the edited rows parameter when the grid data is refreshed...
        if (editedRowsParameter) {
            dispatch(updateParameterValue(editedRowsParameter, []));
        }
    }, [rows]);

    const serverModeActive = !!getRows && !!serverSidePagination;
    const [paginationModel, setPaginationModel] = React.useState<GridPaginationModel>({ page: 0, pageSize: initialState?.pagination?.paginationModel?.pageSize ?? 100 });
    const [sortModel, setSortModel] = React.useState<GridSortModel>([]);
    const [rowCount, setRowCount] = React.useState(0);
    const { data: fetchedResult, isLoading, hasLoaded, load } = useLazyData<GetRowsResult>({ fetcher: getRows, errorMessage: "Failed to load table rows." });

    React.useEffect(() => {
        if (getRows && rows) {
            reportValidationError("Both `rows` and `getRows` were provided - `rows` will be ignored.");
        }

        if (serverSidePagination && !getRows) {
            reportValidationError("`serverSidePagination` requires `getRows` and will be ignored.");
        }
    }, []);

    // Lazy one-shot fetch (no server-side pagination)...
    React.useEffect(() => {
        if (!getRows || serverModeActive) return;

        load();
    }, []);

    // Server-side pagination - fetch on mount and whenever the page/page size/sort order changes...
    const orderBy = React.useMemo(() => (sortModel ?? []).filter(s => !!s.sort).map(s => ({ field: s.field, sort: s.sort })), [sortModel]);
    React.useEffect(() => {
        if (!serverModeActive) return;

        load({ page: paginationModel.page, pageSize: paginationModel.pageSize, orderBy });
    }, [serverModeActive, paginationModel.page, paginationModel.pageSize, JSON.stringify(orderBy)]);

    // Apply fetched data through the same mirror as the `rows` prop so that the editing pipeline behaves identically...
    React.useEffect(() => {
        if (!getRows || !hasLoaded) return;

        const normalized = normalizeGetRowsResult(fetchedResult);
        setCurrentRows(normalized.rows);
        setRowCount(normalized.rowCount);

        // Clear out the edited rows parameter when the grid data is refreshed...
        if (editedRowsParameter) {
            dispatch(updateParameterValue(editedRowsParameter, []));
        }
    }, [fetchedResult, hasLoaded]);

    const handlePaginationModelChange = React.useCallback((model: GridPaginationModel, details: GridCallbackDetails<"pagination", GridApiPro>) => {
        setPaginationModel(model);
        onPaginationModelChange?.(model, details);
    }, [onPaginationModelChange]);

    const handleSortModelChange = React.useCallback((model: GridSortModel, details: GridCallbackDetails<any, GridApiPro>) => {
        setSortModel(model);
        // Jump back to the first page whenever the sort order changes...
        setPaginationModel(p => ({ ...p, page: 0 }));
        onSortModelChange?.(model, details);
    }, [onSortModelChange]);

    const handleRowClick = React.useCallback(
        (params: GridRowParams, event: MuiEvent<React.MouseEvent>, details: GridCallbackDetails) => {
            if (onRowClick) {
                const result = onRowClick(params, event, details) ?? true;

                if (!result) {
                    return;
                }
            }

            const data = params.row;

            if (parameterMap?.length && navigateTo) {
                const parameterObject = parameterMap.reduce((acc, elem) => {
                    acc[elem.parameterName] = data[elem.fieldName];
                    return acc;
                }, {} as Record<string, any>);
                navigateToPage(navigateTo, parameterObject, true);
            }

        },
        [parameterMap, navigateTo, navigateToPage, parameterValues, updateParameterValue, onRowClick]
    );

    // handle the possibility of multiple row selection
    const handleRowSelectionModelChange = React.useCallback(
        (rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails<any, GridApiPro>) => {
            const selectedRows: GridRowParams[] = rowSelectionModel.ids ? Array.from(rowSelectionModel.ids).map(id => apiRef.current.getRow(id)) : [];
            const parameterToUpdate = selectedRowsParameter ?? selectedRowParameter;
            // normalize to single or multiple selection based on is_multiselect
            const selectedRowsBySelectionType = (
                !disableMultipleRowSelection 
                    ? selectedRows : 
                    selectedRows.length > 0 
                        ? selectedRows[0] 
                        : null
            );

            if (parameterToUpdate) {
                dispatch(updateParameterValue(parameterToUpdate, selectedRowsBySelectionType));
            }

            if (onRowSelectionModelChange) {
                onRowSelectionModelChange(rowSelectionModel, details);
            }

            // always update the ui state
            setRowSelectionModel(rowSelectionModel);
        }, [disableMultipleRowSelection, apiRef, dispatch, onRowSelectionModelChange]
    );

    const processRowUpdate = React.useCallback(
        (updatedRow: GridRowModel, originalRow: GridRowModel, params: {
            rowId: GridRowId;
        }): GridRowModel => {
            // If the value hasn't actually changed, skip the below...
            if (_.isEqual(updatedRow, originalRow)) {
                return originalRow;
            }

            // get the edited fields
            const initialStateRow = (getRows ? currentRows : rows)?.find((r) => r.id === params.rowId);
            const editedFields = Object.keys(updatedRow).filter(key => !_.isEqual(updatedRow[key], initialStateRow?.[key]) && key !== "editedFields");
            updatedRow.editedFields = editedFields;
            if (onRowEdited) {
                const result = onRowEdited(
                    updatedRow,
                    originalRow
                ) ?? true;

                // Short-circuit if the 'onRowEdited' handler returned a falsey value...
                if (!result) {
                    return originalRow;
                }
            }
            
            if (editedRowsParameter) {
                const editedRows = parameterValues.get(editedRowsParameter) as GridRowModel[] || [];
                // If this row was edited previously, exclude the old version from the changeset and only include the latest version...
                const editedRowsExcludingCurrentRow = editedRows.filter(r => r.id !== params.rowId);
                dispatch(updateParameterValue(editedRowsParameter, [...editedRowsExcludingCurrentRow, updatedRow]));
            }

            if (allRowsParameter) {
                setTimeout(() => {
                    const gridRows = Array.from(apiRef.current.getRowModels().entries()).map(([_, item]) => item);
                    dispatch(updateParameterValue(allRowsParameter, gridRows));
                }, 50);
            }

            return updatedRow;
        },
        [apiRef, rows, currentRows, getRows, parameterValues, updateParameterValue, onRowEdited]
    );

    // backwards compatibility for name changes: https://mui.com/x/migration/migration-data-grid-v5/#filtering
    const initialFilter: GridFilterInitialState = React.useMemo(() => {
        return addBackwardsCompatibilityToFilterState(initialState?.filter);
    }, [
        initialState?.filter
    ]);
    // Note: this also effects `filterFormProps` and `localeText` but these are not really used...

    return (
        <DataGrid
            apiRef={apiRef}
            pinnedColumnsSectionSeparator="border"
            pinnedRowsSectionSeparator='border'
            {...other}
            {...(serverModeActive ? {
                pagination: true,
                paginationMode: "server" as const,
                sortingMode: "server" as const,
                paginationModel,
                sortModel,
                rowCount,
            } : {})}
            onPaginationModelChange={serverModeActive ? handlePaginationModelChange : onPaginationModelChange}
            onSortModelChange={serverModeActive ? handleSortModelChange : onSortModelChange}
            loading={isLoading || props.loading}
            className={clsx("table-component", navigateTo && "navigate-on-click", className)}
            slots={{
                footer: GridFooter,
                baseCheckbox: GridCheckBox,
                ...slots
            }}
            slotProps={{
                ...slotProps,
                footer: {
                    hideExportToExcel: props.hideExportToExcel,
                    additionalFooterItems,
                    ...slotProps?.footer
                },
            }}
            columns={allColumns}
            rows={currentRows || []}
            processRowUpdate={processRowUpdate}
            onRowClick={handleRowClick}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            rowSelectionModel={rowSelectionModel}
            disableMultipleRowSelection={disableMultipleRowSelection}
            initialState={{
                density: sanitizeDensityValue(density),
                ...initialState,
                filter: initialFilter,
            }}
            columnHeaderHeight={headerHeight ?? columnHeaderHeight ?? 56}
        />
    );
}

(Table as DocumentedComponent).injectSourceInfo = true;

const COLUMN_PROPS = `### \`Column\` Props:

| Name | Type | Description |
|------|------|-------------|
| \`field\` | \`string\` | The name of the field to display in this column.  Corresponds to a key in the row object. |
| \`headerName\` | \`string\` | The title of the column rendered in the column header cell. |
| \`description\` | \`string?\` | The tooltip to display when the user hovers over the column header. |
| \`type\` | \`'string' \\| 'number' \\| 'date' \\| 'dateTime' \\| 'boolean'\` | The type of data that is displayed in the column.  Determines how to display the data (alignment, formatting, controls) and which filters to expose in the data filter dialog. |
| \`flex\` | \`number?\` | If set, it indicates that a column has fluid width.  All columns with a \`flex\` value populated will share the available horizontal space according to this ratio.  Optional - defaults to \`null\`. |
| \`width\` | \`number?\` | The initial width of the column in \`px\`.  The \`flex\` property above takes precedence over this value, if populated.  Optional - defaults to \`100\`. |
| \`minWidth\` | \`number?\` | The minimum width of the column.  Applies to both calculated \`flex\` sizes and user interaction (column resizing).  Optional - defaults to \`50\`. |
| \`editable\` | \`boolean?\` | If \`true\`, the user can edit the cell data inline by double-clicking on the cell.  Optional - defaults to \`false\`. |
| \`cellClassName\` | \`string\` \| \`(params: GridCellParams) => string\` | The optional class name to apply to the cell.  Can be either a string (shared across all rows) or a callback function which returns the class name(s) for the current cell.  The callback function's input parameter has a field called \`value\`, containing the current cell's field value and one called \`row\`, containing the source data for the current row. For details of the parameters go to: https://mui.com/x/api/data-grid/grid-cell-params/ |
| \`renderCell\` | \`(params: GridRenderCellParams) => ReactNode\` | The optional render function to use for rendering the cell body.  The input parameter has a field called \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. For details of the parameters go to: https://mui.com/x/api/data-grid/grid-cell-params/ |
| \`renderEditCell\` | \`(params: GridRenderEditCellParams) => ReactNode\` | The optional render function to use for rendering the cell body when in edit mode.  The input parameter has a field called \`api\`, which can be used to stage the latest changes, \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. For details of the parameters go to: https://mui.com/x/api/data-grid/grid-cell-params/ |
| \`renderTooltip\` | \`(params: GridRenderCellParams) => ReactNode\` | The optional render function to use for rendering the cell's tooltip.  The input parameter has a field called \`value\`, containing the current cell value and one called \`row\`, containing the source data for the current row. For details of the parameters go to: https://mui.com/x/api/data-grid/grid-cell-params/ |
| \`valueFormatter\` | \`({ value: any, row: GridRowModel, column: GridColDef, apiRef: GridApiPro }) => string\` | The optional formatter function to use for cell contents.  \`cellTemplate\` takes precedence over this field. |`;

(Table as DocumentedComponent).metadata = {
    description:
        `The \`Table\` component allows for the display of tabular data in a grid.  This is the preferred component for displaying tabular data.

        See [the base component's documentation](https://mui.com/components/data-grid/) for the full feature list.`,
    isSelfClosing: true,
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },

        {
            name: "rows",
            type: "object",
            exactType: "{ [field: string]: any }[]",
            description: "The data to display in the grid.  Each row entry must be an object containing a superset of the fields defined in `columns`.  Alternatively, use `getRows` to fetch the data lazily."
        },
        {
            name: "getRows",
            type: "function",
            exactType: "(args?: { page: number, pageSize: number, orderBy: { field: string, sort: 'asc' | 'desc' }[] }) => ({ [field: string]: any }[] | { rows: { [field: string]: any }[], rowCount: number }) | Promise<{ [field: string]: any }[] | { rows: { [field: string]: any }[], rowCount: number }>",
            template: `getRows={(args) => $1}`,
            description:
                `A callback function which lazily fetches the grid data.  Serves as an alternative to \`rows\` - if both are provided, \`getRows\` takes precedence and \`rows\` is ignored.

The callback may return the data directly, or a \`Promise\` resolving to it.  **Note:** \`async\` arrow functions are not supported by the template parser - write a regular arrow function which *returns* a \`Promise\` instead, e.g. \`getRows={(args) => fetchDatasourceByName("myDatasource", args)}\`.

When \`serverSidePagination\` is enabled, the callback is invoked with a single \`{ page, pageSize, orderBy }\` argument (and re-invoked whenever the page, page size or sort order changes) and should return an object of shape \`{ rows, rowCount }\`, where \`rowCount\` is the total number of rows across all pages.  Without \`serverSidePagination\`, the callback is invoked once (without arguments) and may return the rows array directly.

An unobtrusive loading overlay is displayed while the fetch is in progress.  If the fetch fails, an error notification is shown and the grid renders without rows.`
        },
        {
            name: "serverSidePagination",
            type: "boolean",
            description: "Set this property to `true` in order to enable server-side pagination and sorting.  Requires `getRows` to be provided - the callback receives `{ page, pageSize, orderBy }` and must return `{ rows, rowCount }`.  Note that client-side filtering only applies to the currently loaded page in this mode.  Defaults to `false`."
        },
        {
            name: "columns",
            type: "object",
            exactType: "{ [field: string]: { field: string, headerName: string, description?: string, type: 'string' | 'number' | 'date' | 'dateTime' | 'boolean', flex?: number, width?: number, minWidth?: number, editable?: boolean, renderCell?: (params: GridRenderCellParams) => React.ReactNode, renderEditCell?: (params: GridRenderEditCellParams) => React.ReactNode, renderTooltip?: (params: GridRenderCellParams) => React.ReactNode, valueFormatter?: ({ value: any, row: GridRowModel, column: GridColDef, apiRef: GridApiPro }) => string } }[]",
            description:
                `The columns to display in the grid.  See below for the definition of the \`Column\` type.
            
${COLUMN_PROPS}`
        },
        {
            name: "additionalColumns",
            type: "object",
            exactType: "{ [field: string]: { field: string, headerName: string, description?: string, type: 'string' | 'number' | 'date' | 'dateTime' | 'boolean', flex?: number, width?: number, minWidth?: number, editable?: boolean, renderCell?: (params: GridRenderCellParams) => React.ReactNode, renderEditCell?: (params: GridRenderEditCellParams) => React.ReactNode, renderTooltip?: (params: GridRenderCellParams) => React.ReactNode, valueFormatter?: ({ value: any, row: GridRowModel, column: GridColDef, apiRef: GridApiPro }) => string } }[]",
            description:
                `Additional columns to display in the grid.  This collection is appended to \`columns\` before rendering occurs.  This split allows for some columns to be dynamically defined in the data source and the rest to be defined in the template itself.
                
${COLUMN_PROPS}`
        },
        { name: "pinnedRows", type: "object", exactType: "{ bottom?: { [field: string]: any }[], top?: { [field: string]: any }[] }", description: "Rows which will be pinned to the top/bottom of the grid.  Each row entry must be an object containing a superset of the fields defined in `columns`." },

        { name: "selectedRowsParameter", type: "string", description: "The name of the parameter to bind the currently selected row(s) to.  The entire row(s) is/are bound to this parameter and can be decomposed where the parameter is used." },
        { name: "allRowsParameter", type: "string", description: "The name of the parameter to bind the current grid rows to.  This will include both edited (latest version) and unedited rows." },
        { name: "editedRowsParameter", type: "string", description: "The name of the parameter to bind the edited grid rows to.  This parameter will be populated with the latest version of all rows that were edited by the user." },

        { name: "onRowClick", type: "function", exactType: "(param: MUI.GridRowParams, event?: MUI.MuiEvent<React.MouseEvent>, details?: MUI.GridCallbackDetails) => void | boolean", template: `onRowClick={(params, _event, _details) => {const { row } = params; $1}}`, description: "The callback function to execute when a grid row is clicked on.  The param input parameter contains a field called `row`, which can be used to access the row data. The event parameter contains the mouse event information, and the details parameter contains additional information about the grid state using the Mui.DataGrid api.  If a \`falsey\` value is returned, the parameter value update is prevented." },
        { name: "onRowSelectionModelChange", type: "function", exactType: "(rowSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void", template: `onRowSelectionModelChange={(rowSelectionModel, details) => {const { ids } = rowSelectionModel; $1}}`, description: "The callback function to execute when the grid row selection model changes.  The first input parameter contains the new row selection model, which houses the ids of the selected row in a set at `rowSelectionModel.ids`.  The second input parameter contains additional information about the grid state using the Mui.DataGrid api." },
        { name: "disableMultipleRowSelection", type: "boolean", description: "Set this property to `true` in order to disable multiple row selection, even when `checkboxSelection` is set to `true`.  Defaults to `true`." },
        { name: "checkboxSelection", type: "boolean", description: "Set this property to `true` in order to show a checkbox in each row for selection.  Note that this enables multiple row selection by default.  If you wish to disable multiple row selection, set `disableMultipleRowSelection` to `true`. Note: multi-select requires pressing `Ctrl` (or `cmd` on Mac) for multi-selection if `checkboxSelection` is `false`.  Defaults to `false`." },
        { name: "onRowEdited", type: "function", exactType: "(row: GridRowModel & { editedFields: string[] }, originalRow: GridRowModel) => void | boolean", template: `onRowEdited={(row, originalRow) => {$1}}`, description: "The callback function to execute after a grid row/cell has been edited.  The first input parameter is the row data after the update has occurred.  The second input parameter is the row data just before the update has been applied.  If a \`falsey\` value is returned, the edit operation is cancelled and the changes are reverted." },

        { name: "hideFooter", type: "boolean", description: "Set this property to `true` in order to hide the table footer.  Defaults to `false`." },
        { name: "hideExportToExcel", type: "boolean", description: "Set this property to `true` in order to hide the \"Export to Excel\" button in the table footer.  Defaults to `false`." },
        { name: "additionalFooterItems", type: "object", exactType: "React.ReactNode", description: "Additional items to display in the table footer.  If provided, these items are displayed alongside the \"Export to Excel\" button." },

        { name: "density", type: "string", exactType: "'comfortable' | 'compact' | 'standard'", options: ["comfortable", "compact", "standard"], description: "Determines the heading and row height for the table.  Defaults to `\"standard\"``." },
    ]
};

export default Table;